import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { TfActivity, useDefaultTeamSeasonDates } from 'api';
import { subDays } from 'date-fns';
import { cloneDeep, isEqual } from 'lodash';
import mixpanel from 'mixpanel-browser';
import { ACTIONS, EVENTS } from '../../../analytics/analytics-events';
import { DATE_FNS_CASUAL_DATE_FORMAT } from '../../constants';
import {
    getNextDayOccurence,
    getSecondsDurationBetweenDates,
    getStartOfDay,
    MONDAY,
    useDateFormatter,
} from '../../utils';
import { useDebouncedCallback } from 'reporting/hooks/useDebouncedCallback';
import { REPORT_TYPE } from 'reporting/constants';
import { useDecodedSearchParams, useModifySearchParameters } from 'reporting/hooks/url-manipulation';
import moment from 'moment';
import { UserPermissionsEnum } from 'oas';
import { usePermission } from 'auth';
import { DateRange } from '../activity-side-menu-filters';
import { BaseReportType } from 'reporting/types';

const DEFAULT_MAX_SELECTION = 100;
const DEFAULT_MATCH_DAY_SELECTION = 7;

interface UseMultiActivityLogicProps {
    selectedActivities: TfActivity[];
    setSelectedActivities: (activities: TfActivity[]) => void;
    activitiesList: TfActivity[];
    setActivitiesList: (activities: TfActivity[]) => void;
    flattenedResults?: TfActivity[];
    weeksToPreselect?: number;
    reportType: BaseReportType;
    setActivitiesLoading?: (loading: boolean) => void;
    isLoading: boolean;
    dateFilters: DateRange;
}

export const useMultiActivityLogic = ({
    selectedActivities,
    setSelectedActivities,
    activitiesList,
    setActivitiesList,
    flattenedResults,
    weeksToPreselect,
    reportType,
    setActivitiesLoading,
    isLoading,
    dateFilters,
}: UseMultiActivityLogicProps) => {
    const [dateRangePlaceholder, setDateRangePlaceholder] = useState('');
    const [selectedItems, setSelectedItems] = useState<TfActivity[]>(selectedActivities ?? []);
    const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
    const [indicesLoaded, setIndicesLoaded] = useState<boolean>(false);
    const [lastClickedIndex, setLastClickedIndex] = useState<number | null>(null);

    const seasonData = useDefaultTeamSeasonDates();
    const isSharedLinkLogin = usePermission(UserPermissionsEnum.SharedLink);
    const modifySearchParams = useModifySearchParameters();
    const decodeSearchParams = useDecodedSearchParams();
    const formatDate = useDateFormatter();
    const { search } = useLocation();

    const shouldAutomaticallyPreselect = useRef(true);
    const isInitialMount = useRef(true);
    const prevDateFiltersRef = useRef(dateFilters);

    // Update activities list
    useEffect(() => {
        if (activitiesList.length !== flattenedResults?.length) {
            setActivitiesList(flattenedResults ?? []);
        }
    }, [activitiesList.length, flattenedResults, setActivitiesList]);

    // Loading state
    useEffect(() => {
        setActivitiesLoading?.(isLoading);
    }, [isLoading, setActivitiesLoading]);

    // Debounced update
    const updateSelectedActivitiesDebounced = useDebouncedCallback(
        (sortedSelectedActivities: TfActivity[]) => {
            setSelectedActivities(sortedSelectedActivities);
        },
        [setSelectedActivities],
        1500,
    );

    const updateSelectedActivities = useCallback(
        (selectedActivitiesUnsorted: TfActivity[]) => {
            const activitiesSorted = selectedActivitiesUnsorted?.sort((first, second) =>
                getSecondsDurationBetweenDates(first.start_time, second.start_time),
            );
            setSelectedItems(activitiesSorted);
            updateSelectedActivitiesDebounced?.(activitiesSorted);
        },
        [updateSelectedActivitiesDebounced],
    );

    // Reset preselection flag
    useEffect(() => {
        if (selectedActivities.length === 0) {
            shouldAutomaticallyPreselect.current = true;
        }
    }, [selectedActivities]);

    // Handle preselecting activities
    useEffect(() => {
        const hasResults = flattenedResults && flattenedResults.length > 0;
        if (!hasResults || !shouldAutomaticallyPreselect.current) {
            return;
        }

        let newSelectedActivities: TfActivity[] = [];
        let newDateRangePlaceholder: string | undefined;

        if (reportType === REPORT_TYPE.longitudinal || reportType === REPORT_TYPE.weekly) {
            const scope = weeksToPreselect && weeksToPreselect > 0 ? weeksToPreselect : 1;
            const mostRecentActivityDate = flattenedResults[0]?.start_time ?? new Date();
            const morningMostRecentActivityDate = getStartOfDay(mostRecentActivityDate) ?? mostRecentActivityDate;
            let weeksAgo = subDays(morningMostRecentActivityDate, scope * 7);

            if (reportType === REPORT_TYPE.weekly) {
                const weekStartDay =
                    seasonData?.length !== 0 ? new Date((seasonData?.[0].value ?? 0) * 1000).getDay() : MONDAY;
                weeksAgo = getNextDayOccurence(
                    weekStartDay,
                    moment(morningMostRecentActivityDate).subtract(weeksToPreselect, 'weeks').toDate(),
                );
            }

            newSelectedActivities =
                flattenedResults
                    ?.filter((activity) => activity.start_time > weeksAgo)
                    .slice(0, DEFAULT_MAX_SELECTION) ?? [];
            newDateRangePlaceholder = `${formatDate(weeksAgo, DATE_FNS_CASUAL_DATE_FORMAT)} - ${formatDate(
                mostRecentActivityDate,
                DATE_FNS_CASUAL_DATE_FORMAT,
            )}`;
        } else {
            newSelectedActivities = cloneDeep(flattenedResults)?.slice(0, DEFAULT_MATCH_DAY_SELECTION) ?? [];
        }

        if (!isEqual(selectedActivities, newSelectedActivities)) {
            updateSelectedActivities(newSelectedActivities);
            if (newDateRangePlaceholder) {
                setDateRangePlaceholder(newDateRangePlaceholder);
            }
            shouldAutomaticallyPreselect.current = false;
        }
    }, [
        flattenedResults,
        formatDate,
        reportType,
        selectedActivities,
        weeksToPreselect,
        updateSelectedActivities,
        seasonData,
    ]);

    // URL sync
    useEffect(() => {
        if (activitiesList[0]) {
            modifySearchParams('date-to', new Date(activitiesList?.[0]?.start_time).toISOString().split('T')[0]);
        }
        modifySearchParams('activities', selectedIndices.join(','));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIndices, activitiesList]);

    // Force selected indices to re-settle if invalid
    useEffect(() => {
        const selectedQuery = decodeSearchParams('activities')?.split(',');

        if (selectedQuery) {
            for (let x = 0; x < selectedQuery?.length; x++) {
                const idx = parseInt(selectedQuery[x], 10);

                if (idx > activitiesList.length - 1 || selectedQuery.length < selectedActivities.length) {
                    const indices = activitiesList
                        .map((selected) => selectedActivities.findIndex((a) => a.id === selected.id))
                        .filter((i) => i !== -1);
                    setSelectedIndices(indices);
                    break;
                }
            }
        }
    }, [selectedActivities, activitiesList, decodeSearchParams]);

    // Load indices from URL
    useEffect(() => {
        const selectedQuery = decodeSearchParams('activities')?.split(',');
        const idxs: number[] = [];

        if (selectedQuery) {
            if (!indicesLoaded) {
                for (let i = 0; i < selectedQuery?.length; i++) {
                    const idx = parseInt(selectedQuery[i], 10);

                    if (idx > -1) {
                        idxs.push(idx);
                    }
                }

                setSelectedIndices(idxs);

                if (activitiesList.length > 0) {
                    const items =
                        activitiesList?.filter((_tfActivity: TfActivity, i: number) =>
                            idxs.filter((idx) => idx <= activitiesList.length - 1).some((j) => i === j),
                        ) ?? [];

                    if (items.length === 0) {
                        items.push(activitiesList[0]);
                        setSelectedIndices([0]);
                    }

                    setIndicesLoaded(true);
                    updateSelectedActivities(items);
                }
            }
        } else if (selectedActivities.length > 0) {
            const indices = activitiesList
                .map((selected) => selectedActivities.findIndex((a) => a.id === selected.id))
                .filter((i) => i !== -1);
            setSelectedIndices(indices);
        }
    }, [selectedActivities, updateSelectedActivities, indicesLoaded, activitiesList, search, decodeSearchParams]);

    // When dateFilters change, select all filtered activities for longitudinal report
    useEffect(() => {
        if (reportType !== REPORT_TYPE.longitudinal) {
            return;
        }

        if (isInitialMount.current) {
            isInitialMount.current = false;
            prevDateFiltersRef.current = dateFilters;
            return;
        }

        if (isEqual(prevDateFiltersRef.current, dateFilters)) {
            return;
        }

        updateSelectedActivities(activitiesList);
        prevDateFiltersRef.current = dateFilters;
    }, [dateFilters, activitiesList, reportType, updateSelectedActivities]);

    // Check if activity is selected
    const isSelected = useCallback(
        (activity: TfActivity): boolean => {
            if (selectedItems[0]) {
                localStorage.latestSelected = selectedItems[0].id;
            }

            return !!selectedItems?.find((tfActivity) => tfActivity.id === activity?.id);
        },
        [selectedItems],
    );

    // Handle selection/deselection with shift and cmd/ctrl support
    const handleSelection = useCallback(
        (activity: TfActivity, index: number, isShiftClick = false, isMetaClick = false) => {
            const shouldDeselect = isSelected(activity);
            const indices = [...selectedIndices];

            // Shift+click range selection
            if (isShiftClick && !isMetaClick) {
                // Determines anchor index, prefer last clicked, else highest selected, else current
                const anchorIndex =
                    lastClickedIndex !== null
                        ? lastClickedIndex
                        : selectedIndices.length > 0
                        ? Math.max(...selectedIndices)
                        : index;

                const start = Math.min(anchorIndex, index);
                const end = Math.max(anchorIndex, index);
                const rangeIndices = Array.from({ length: end - start + 1 }, (_, i) => start + i);

                // Select range: add all indices in the range
                rangeIndices.forEach((i) => {
                    if (!indices.includes(i)) {
                        indices.push(i);
                    }
                });

                setSelectedIndices(indices);

                // Get all activities in the range
                const rangeActivities = flattenedResults?.filter((_, i) => rangeIndices.includes(i)) ?? [];
                const newSelectedActivities = [...selectedItems];

                rangeActivities.forEach((act) => {
                    if (!selectedItems.find((item) => item.id === act.id)) {
                        newSelectedActivities.push(act);
                    }
                });

                updateSelectedActivities(newSelectedActivities);

                mixpanel.track(EVENTS.sideMenu.activitySelection, {
                    activityId: activity.id,
                    selectionType: 'range-select',
                    rangeSize: rangeIndices.length,
                });

                // Update anchor for next shift+click
                setLastClickedIndex(index);
            } else if (isMetaClick) {
                // Cmd/Ctrl+click: range deselection
                // Determine anchor index same as shift+click
                const anchorIndex =
                    lastClickedIndex !== null
                        ? lastClickedIndex
                        : selectedIndices.length > 0
                        ? Math.max(...selectedIndices)
                        : index;

                const start = Math.min(anchorIndex, index);
                const end = Math.max(anchorIndex, index);
                const rangeIndices = Array.from({ length: end - start + 1 }, (_, i) => start + i);

                // Deselect range: remove all indices in the range
                rangeIndices.forEach((i) => {
                    const idx = indices.indexOf(i);
                    if (idx >= 0) {
                        indices.splice(idx, 1);
                    }
                });

                setSelectedIndices(indices);

                // Remove activities in range from selected items
                const rangeActivityIds =
                    flattenedResults?.filter((_, i) => rangeIndices.includes(i)).map((act) => act.id) ?? [];

                updateSelectedActivities(selectedItems.filter((item) => !rangeActivityIds.includes(item.id)));

                mixpanel.track(EVENTS.sideMenu.activitySelection, {
                    activityId: activity.id,
                    selectionType: 'range-deselect',
                    rangeSize: rangeIndices.length,
                });

                // Update anchor for next click
                setLastClickedIndex(index);
            } else {
                // Normal single click selection
                if (shouldDeselect) {
                    const i = indices.indexOf(index);
                    if (i >= 0) {
                        indices.splice(i, 1);
                    }
                } else {
                    indices.push(index);
                }

                setSelectedIndices(indices);

                mixpanel.track(EVENTS.sideMenu.activitySelection, {
                    activityId: activity.id,
                    selectionType: 'multiple',
                    action: shouldDeselect ? ACTIONS.deselected : ACTIONS.selected,
                });

                if (shouldDeselect) {
                    selectedItems.length !== 1 &&
                        updateSelectedActivities(
                            selectedItems?.filter((tfActivity: TfActivity) => tfActivity.id !== activity.id) ?? [],
                        );
                } else {
                    updateSelectedActivities([...selectedItems, activity]);
                }

                setLastClickedIndex(index);
            }
        },
        [isSelected, selectedIndices, selectedItems, updateSelectedActivities, lastClickedIndex, flattenedResults],
    );

    return useMemo(
        () => ({
            dateRangePlaceholder,
            indicesLoaded,
            shouldRenderChildrenOnly: isSharedLinkLogin,
            handleSelection,
            isSelected,
        }),
        [dateRangePlaceholder, indicesLoaded, isSharedLinkLogin, handleSelection, isSelected],
    );
};
